import React, { useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import taskService from '../../services/taskService';
import referenceFileService from '../../services/referenceFileService';
import UserPicker from './UserPicker';
import '../../assets/styles/Workflow.css';

interface AddSubtaskModalProps {
  projectId: number;
  parentTaskId?: number;
  projectSummary: string;
  /** Project's own due date (YYYY-MM-DD) - a subtask's due date can never run later than this. */
  maxDueDate?: string;
  onClose: () => void;
  onCreated: () => void;
}

// Standalone Add Subtask modal, styled after the FDC Ops prototype's
// overlay/modal markup (see FDCCreateWizard.tsx for the wf-overlay/wf-modal
// pattern this mirrors). Unlike the prototype's flat "assign to anyone"
// model, the real backend requires a build/qa `kind` on every subtask
// (SubtaskCreateRequest.kind has no default) - kept as a labeled field
// instead of the prototype's plain assign-to select.
//
// Every subtask - first or fifth, flat or nested - works identically: the
// PC Manager freely picks kind and assignee, never forced. There's no
// "root"/first task with special, locked-in content.
//
// Assignee can be anyone - including the PC Manager delegating it, i.e.
// themselves - except admin and Business Development (BD only creates/
// edits/views projects, never does subtask-level work).
const SUBTASK_ASSIGNEE_EXCLUDE_ROLES = ['admin', 'Business Development'];

const AddSubtaskModal: React.FC<AddSubtaskModalProps> = ({
  projectId, parentTaskId, projectSummary, maxDueDate, onClose, onCreated,
}) => {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<'build' | 'qa' | 'deliver' | 'other'>('build');
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [requirements, setRequirements] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Input files for whoever picks up this subtask - staged client-side until
  // the subtask itself exists (same pattern as the project wizard's own
  // reference files), then uploaded tagged to its new id right after create.
  // Visible only to this subtask's own members, never the whole project -
  // see the backend model's docstring.
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [dedupFile, setDedupFile] = useState<File | null>(null);
  const [otherFile, setOtherFile] = useState<File | null>(null);

  const submit = async () => {
    if (!assigneeId) {
      setError('Pick an assignee for the subtask');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const created = await taskService.createSubtask({
        project_id: projectId,
        parent_task_id: parentTaskId ?? null,
        kind,
        assignee_id: assigneeId,
        title: title || undefined,
        requirements: requirements || undefined,
        priority,
        due_date: dueDate || undefined,
      });

      const uploads: Promise<any>[] = [];
      if (sampleFile) uploads.push(referenceFileService.upload(projectId, sampleFile, created.id, 'sample'));
      if (dedupFile) uploads.push(referenceFileService.upload(projectId, dedupFile, created.id, 'dedup'));
      if (otherFile) uploads.push(referenceFileService.upload(projectId, otherFile, created.id, 'other'));
      if (uploads.length > 0) {
        // Best-effort - the subtask itself is already created at this point,
        // so a file upload failure shouldn't be reported as the whole
        // "Add subtask" action having failed.
        await Promise.allSettled(uploads);
      }

      onCreated();
      onClose();
    } catch (e: any) {
      setError((e?.response?.data?.message || e?.response?.message) || 'Failed to create subtask');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wf-overlay">
      <div className="wf-modal">
        <div className="wf-modal-head">
          <h2 style={{ fontSize: 17 }}>Add subtask - {projectSummary}</h2>
          <button className="wf-iconbtn" onClick={onClose}>✕</button>
        </div>
        <div className="wf-modal-body">
          <div className="wf-stack wf-form">
            {error && <div style={{ color: '#ef4444', fontSize: 13 }}>{error}</div>}
            <div>
              <label className="wf-field-label">Subtask title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Source and de-dup raw list"
              />
            </div>
            <div>
              <label className="wf-field-label">Type of Task</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as 'build' | 'qa' | 'deliver' | 'other')}>
                <option value="build">Build (PC)</option>
                <option value="qa">QA review</option>
                <option value="deliver">Deliver (Fulfillment)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="wf-field-label">Assign to<span style={{ color: '#ef4444' }}> *</span></label>
              <UserPicker
                excludeRoles={SUBTASK_ASSIGNEE_EXCLUDE_ROLES}
                value={assigneeId}
                onChange={(id) => setAssigneeId(id)}
                placeholder="Assign to..."
              />
            </div>
            <div className="wf-grid2">
              <div>
                <label className="wf-field-label">Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="wf-field-label">Due date (SLA)</label>
                <input
                  type="date"
                  max={maxDueDate}
                  value={dueDate}
                  onChange={(e) => {
                    if (maxDueDate && e.target.value > maxDueDate) return;
                    setDueDate(e.target.value);
                  }}
                />
                {maxDueDate && <div className="wf-field-hint">Can't run later than the project's own due date ({maxDueDate}).</div>}
              </div>
            </div>
            <div>
              <label className="wf-field-label">Additional Notes</label>
              <div className="wf-requirements-editor">
                <CKEditor
                  editor={ClassicEditor as any}
                  data={requirements}
                  onChange={(_, editor) => setRequirements(editor.getData())}
                  config={{ placeholder: 'Instructions for whoever picks this up...' }}
                />
              </div>
            </div>
            <div>
              <label className="wf-field-label">Input Files</label>
              <div className="wf-field-hint" style={{ marginBottom: 8 }}>
                Only visible to whoever's on this subtask - not shown in the project's general file list.
              </div>
              <div className="wf-grid2" style={{ gap: 10 }}>
                <div>
                  <label className="wf-field-label" style={{ fontWeight: 500, textTransform: 'none' }}>Sample File</label>
                  <input type="file" onChange={(e) => setSampleFile(e.target.files?.[0] || null)} />
                </div>
                <div>
                  <label className="wf-field-label" style={{ fontWeight: 500, textTransform: 'none' }}>De-duplication File</label>
                  <input type="file" onChange={(e) => setDedupFile(e.target.files?.[0] || null)} />
                </div>
                <div>
                  <label className="wf-field-label" style={{ fontWeight: 500, textTransform: 'none' }}>Other Relevant Reference Files</label>
                  <input type="file" onChange={(e) => setOtherFile(e.target.files?.[0] || null)} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="wf-modal-foot">
          <span />
          <button className="wf-btn wf-btn-primary" disabled={submitting} onClick={submit}>
            {submitting ? 'Adding...' : 'Add subtask'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSubtaskModal;
